import { useState } from "react";
import { toast } from "react-toastify";
import { Box, Divider, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  Form,
  Formik,
  FormikErrors,
  FormikTouched,
  FormikValues,
} from "formik";

import AddPatientForm from "./form/AddPatientForm";
import FormStepNavComponent from "../../../common/form/Steps/FormStepComponent";
import StepsHeadingSection from "../../../common/form/Steps/StepsHeadingSection";
import FormStepBottomComponent from "../../../common/form/Steps/FormStepBottomComponent";

import { logger } from "../../../../utils/logger";
import { userStatus } from "../../../../constants/user";
import { useAppDispatch } from "../../../../redux/store";
import patientService from "../../../../services/patient";
import { setLoadingAction } from "../../../../redux/utils/actions";

import RoutingList from "../../../../utils/RoutingList";
import {
  patientAdmissionStepsInfo,
  patientAdmissionValidationSchema,
  patientInitData,
} from "../utils";
import styles from "./../styles.module.scss";

interface ConvertedError {
  [key: string]: boolean;
}

const AddPatient = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { id } = useParams();

  const [steps, setSteps] = useState(0);

  const stepHandler = (
    validateForm: (values: FormikValues) => Promise<any>,
    values: FormikValues,
    stepIndex: number,
    setErrors?: (errors: FormikErrors<FormikValues>) => void,
    setFieldTouched?: (
      field: string,
      isTouched?: boolean,
      shouldValidate?: boolean
    ) => void
  ) => {
    validateForm(values).then((res) => {
      if (Object.entries(res).length === 0) {
        // validation success
        const lastStepIndex = patientAdmissionStepsInfo?.length - 1;
        if (stepIndex <= -1) {
          setSteps(0);
          return;
        } else if (steps === lastStepIndex && stepIndex > lastStepIndex) {
          saveHandler(values, false);
          return;
        } else if (stepIndex <= lastStepIndex) {
          setSteps((prev) => stepIndex);
          return;
        }
      } else {
        // validation failed
        logger.error(res);
        if (setErrors) {
          Object.keys(res).forEach((key) => {
            setFieldTouched?.(key, true);
          });
          setErrors(res);
        }
      }
    });
  };

  const saveHandler = async (values: FormikValues, isDraft: boolean) => {
    try {
      const formValues = {
        ...values,
        isLowerIncomeBeneficiary: !!(
          values?.isLowerIncomeBeneficiary === "true"
        ),
        incomeCertificate:
          values?.isLowerIncomeBeneficiary === "true"
            ? values?.incomeCertificate[0]?.path
            : "",
        age: `${values.year}-${values.month}-${values.day}`,
        status: isDraft ? userStatus.DRAFTED : userStatus.SUBMITTED,
        // card: [values?.adharCard[0], values?.lalCard[0], values?.panCard[0]],
      };
      dispatch(setLoadingAction(true));
      let res;
      if (id) {
        res = await patientService.update(id, formValues);
      } else {
        res = await patientService.create(formValues);
      }
      // dispatch(setLoadingAction(false));
      toast.success(res?.data?.msg);
      navigate(RoutingList?.index);
    } catch (err: any) {
      dispatch(setLoadingAction(false));
      toast.error(err.response.data.msg);
    }
  };

  return (
    <>
      <Box className={styles.boxPadding}>
        <Formik
          enableReinitialize={true}
          validationSchema={patientAdmissionValidationSchema[steps]}
          initialValues={patientInitData}
          onSubmit={(
            values,
            {
              setSubmitting,
              validateForm,
              setFieldError,
              setErrors,
              setFieldTouched,
            }
          ) => {
            stepHandler(
              validateForm,
              values,
              steps > patientAdmissionStepsInfo?.length - 1 ? steps : steps + 1,
              setErrors,
              setFieldTouched
            );
          }}
        >
          {({ values }) => (
            <Form>
              <StepsHeadingSection
                title="Admissions"
                subtitle="Admission"
                isEdit={!!id}
              />
              <FormStepNavComponent
                steps={steps}
                setSteps={setSteps}
                stepHandler={stepHandler}
                stepsInfo={patientAdmissionStepsInfo}
              />

              <Grid container className={styles.gridCenter}>
                <Grid item xs={12} lg={8}>
                  <AddPatientForm steps={steps} />
                  <Divider className={styles.mTopBottom20} />
                  <FormStepBottomComponent
                    steps={steps}
                    setSteps={setSteps}
                    stepsInfo={patientAdmissionStepsInfo}
                    stepHandler={stepHandler}
                    draftFunction={() => saveHandler(values, true)}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Box>
    </>
  );
};

export default AddPatient;
